import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import { axiosInstance, validateUuid } from "../utils";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

export interface CandidateGridPreset {
    id: string;
    name: string;
    isDefault: boolean;
    presetJson: string;
    createdOn: string | null;
}

/** Shape stored in PresetJson for the Candidates grid. */
export interface PresetData {
    columnFilters: ColumnFiltersState;
    sorting: SortingState;
}

export interface CreateCandidateGridPresetPayload {
    name: string;
    presetJson: string;
    isDefault: boolean;
}

export interface UpdateCandidateGridPresetPayload {
    name: string;
    presetJson: string;
    isDefault: boolean;
}

export function useCandidateGridPresets(tenantId: string | undefined) {
    const axios = axiosInstance();
    return useQuery({
        ...queryKeys.candidateGridPresets.byTenantId(tenantId ?? ""),
        enabled: !!tenantId,
        queryFn: () => axios.get<CandidateGridPreset[]>(`api/CandidateGridPresets/${validateUuid(tenantId)}`).then(r => r.data),
        staleTime: 30000,
    });
}

export function useCreateCandidateGridPreset(tenantId: string) {
    const axios = axiosInstance();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: CreateCandidateGridPresetPayload) =>
            axios.post<CandidateGridPreset>(`api/CandidateGridPresets/${validateUuid(tenantId)}`, payload).then(r => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.candidateGridPresets.byTenantId(tenantId).queryKey);
        },
    });
}

export function useUpdateCandidateGridPreset(tenantId: string) {
    const axios = axiosInstance();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ presetId, payload }: { presetId: string; payload: UpdateCandidateGridPresetPayload }) =>
            axios.put<CandidateGridPreset>(`api/CandidateGridPresets/${validateUuid(tenantId)}/${presetId}`, payload).then(r => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.candidateGridPresets.byTenantId(tenantId).queryKey);
        },
    });
}

export function useDeleteCandidateGridPreset(tenantId: string) {
    const axios = axiosInstance();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (presetId: string) => axios.delete(`api/CandidateGridPresets/${validateUuid(tenantId)}/${presetId}`),
        onSuccess: () => {
            queryClient.invalidateQueries(queryKeys.candidateGridPresets.byTenantId(tenantId).queryKey);
        },
    });
}

export function parsePresetData(presetJson: string): PresetData | null {
    try {
        return JSON.parse(presetJson) as PresetData;
    } catch {
        return null;
    }
}
